import { useCallback, useEffect, useState } from "react";
import { Bell, LoaderCircle, RefreshCw, RotateCcw } from "lucide-react";

import { getNotifications, retryNotification } from "./api";
import type { Notification } from "./types";
import { useVisiblePolling } from "./use-visible-polling";

export function NotificationsPage({ canWrite }: { canWrite: boolean }) {
  const [items, setItems] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [retrying, setRetrying] = useState<string>();

  const load = useCallback(async () => {
    try {
      setItems(await getNotifications());
      setFailed(false);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => void load(), [load]);
  useVisiblePolling(load, 30_000);

  async function retry(id: string) {
    setRetrying(id);
    try {
      await retryNotification(id);
      await load();
    } catch {
      setFailed(true);
    } finally {
      setRetrying(undefined);
    }
  }

  return (
    <div className="page-content notifications-page">
      <header className="page-intro">
        <span className="eyebrow lime">Communication delivery</span>
        <h1>Every customer message, visible and accountable.</h1>
        <p>
          Monitor confirmation and internal alert jobs sent from the verified TLC mailbox. Failed
          deliveries remain visible and can be retried by an authorized administrator.
        </p>
      </header>

      {failed ? (
        <div className="load-error" role="alert">
          We couldn’t refresh notification delivery.
          <button onClick={() => void load()}>Try again</button>
        </div>
      ) : null}

      <section className="content-card">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Delivery queue</span>
            <h2>Notifications</h2>
          </div>
          <button className="text-button" onClick={() => void load()} disabled={loading}>
            {loading ? <LoaderCircle className="spin" /> : <RefreshCw />} Refresh
          </button>
        </div>

        {loading && items.length === 0 ? (
          <div className="table-state">
            <LoaderCircle className="spin" /> Loading notifications…
          </div>
        ) : items.length === 0 ? (
          <div className="notification-empty">
            <Bell />
            <strong>No notification jobs yet.</strong>
            <span>New website inquiries will appear here automatically.</span>
          </div>
        ) : (
          <div className="table-wrap">
            <table className="mobile-card-table">
              <thead>
                <tr>
                  <th>Recipient</th>
                  <th>Message</th>
                  <th>Status</th>
                  <th>Attempts</th>
                  <th>Created</th>
                  <th>
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    <td data-label="Recipient">
                      <strong>{item.recipient}</strong>
                      <span>{item.channel}</span>
                    </td>
                    <td data-label="Message">{labelForTemplate(item.template)}</td>
                    <td data-label="Status">
                      <span className={`status status-${item.status.toLowerCase()}`}>
                        {item.status}
                      </span>
                      {item.errorCode ? <span>{item.errorCode}</span> : null}
                    </td>
                    <td data-label="Attempts">
                      {item.attempts} / {item.maxAttempts}
                    </td>
                    <td data-label="Created">{formatDate(item.createdAt)}</td>
                    <td data-label="">
                      {item.status === "FAILED" ? (
                        <button
                          className="row-action"
                          aria-label={`Retry notification to ${item.recipient}`}
                          disabled={!canWrite || retrying === item.id}
                          onClick={() => void retry(item.id)}
                        >
                          {retrying === item.id ? <LoaderCircle className="spin" /> : <RotateCcw />}
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function labelForTemplate(template: string) {
  if (template === "lead_received_customer") return "Customer inquiry confirmation";
  if (template === "new_lead_internal") return "New lead alert";
  return template.replaceAll("_", " ");
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat("en-US", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}
